'use client';

import React, { useState, useMemo } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getSiteRedirectEncodeUrlWithReferrer } from '@/hooks/useSiteRedirect';
import { env } from '@/utils/env';

interface CountrySiteListProps {
  regionLabel?: string;
  isRegionLabelRegular?: boolean;
  onCountryClick?: (shortName: string) => void;
  deviceType: EPlatform;
}

// Check if URL matches current location
const isMatchUrl = (url: string): boolean => {
  return url.includes(env.NEXT_PUBLIC_FULL_URL);
};

const CountrySiteList: React.FC<CountrySiteListProps> = ({
  regionLabel = 'Region:',
  isRegionLabelRegular = false,
  onCountryClick,
  deviceType,
}) => {
  const isWeb = deviceType === EPlatform.WEB;
  const countrySiteList = useStore((state) => state.countrySiteList);
  const enabledCountrySites = useMemo(
    () => countrySiteList?.filter((site) => site.status === 1) || [],
    [countrySiteList],
  );
  const [activeCountry, setActiveCountry] = useState<string>('');

  // Handle country click
  const handleCountryClick = (url: string, shortName: string) => {
    onCountryClick?.(shortName);
    setActiveCountry(shortName);
    setTimeout(() => window.open(getSiteRedirectEncodeUrlWithReferrer(url), '_self'), 500);
  };

  // Don't render if no sites to show
  if (!enabledCountrySites.length) {
    return null;
  }

  return (
    <Box className={`flex items-center py-[12px] ${isWeb ? 'gap-24 font-be' : 'gap-20 font-bb'}`}>
      {/* Region Label */}
      <Box
        className={`whitespace-nowrap flex-shrink-0 ${isRegionLabelRegular ? 'font-br' : ''}`}
        sx={{
          marginRight: isWeb ? '31px' : '0',
          fontSize: isWeb ? 16 : 14,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {regionLabel}
      </Box>

      {/* Countries Container */}
      <Box
        className="notranslate flex items-center gap-5 flex-wrap flex-1"
        translate="no"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          flex: 1,
        }}
      >
        {enabledCountrySites.map((site, index) => (
          <Link
            key={site.country_short_name || index}
            href={isWeb ? site.pc_site_url : site.m_site_url}
            target={'_self'}
            className="relative flex items-center gap-1 cursor-pointer transition-all duration-200 ease-in-out whitespace-nowrap no-underline text-inherit"
            onClick={(e) => {
              e.preventDefault();
              handleCountryClick(
                isWeb ? site.pc_site_url : site.m_site_url,
                site.country_short_name,
              );
            }}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {/* Country Code */}
            <Typography
              variant="body2"
              className={`font-medium ${
                activeCountry === site.country_short_name
                  ? 'text-[#463ae8] border-b-2 border-[#463ae8]'
                  : ''
              }`}
              sx={{
                fontSize: isWeb ? 16 : 14,
                fontFamily: 'inherit',
                fontWeight: 500,
                color: activeCountry === site.country_short_name ? '#463AE8' : 'inherit',
                borderBottom:
                  activeCountry === site.country_short_name ? '2px solid #463AE8' : 'none',
              }}
            >
              {site.country_short_name}
            </Typography>

            {/* Underline */}
            <Box
              className={`absolute -bottom-1 block w-5 h-0.5 bg-white transition-opacity duration-200 ease-in-out ${
                isMatchUrl(isWeb ? site.pc_site_url : site.m_site_url) ? 'opacity-100' : 'opacity-0'
              }`}
              sx={{
                position: 'absolute',
                bottom: '-4px',
                display: 'block',
                width: '20px',
                height: '2px',
                backgroundColor: '#fff',
                // opacity: isMatchUrl(isWeb ? site.pc_site_url : site.m_site_url) ? 1 : 0,
                transition: 'opacity 0.2s ease',
              }}
            />
          </Link>
        ))}
      </Box>
    </Box>
  );
};

export default CountrySiteList;
